import type { ValidationWarning } from '@/types/device'

/**
 * Validates if a URL is a valid Cloudinary image URL
 */
export const isValidCloudinaryUrl = (url: string): boolean => {
  return typeof url === 'string' && url.startsWith('https://res.cloudinary.com/')
}

/**
 * Validates if a URL is a Cloudinary media URL (image or video)
 */
export const isValidCloudinaryMediaUrl = (url: string): boolean => {
  if (typeof url !== 'string' || !url.startsWith('https://res.cloudinary.com/')) {
    return false
  }

  return /\/(image|video)\/upload\//.test(url)
}

/**
 * Detects whether a URL is likely to be a video asset.
 */
export const isLikelyVideoUrl = (url: string): boolean => {
  if (!url || typeof url !== 'string') return false

  return /\/video\/upload\//.test(url) || /\.(mp4|webm|mov|m4v)(\?|$)/i.test(url)
}

/**
 * Detects whether a URL is likely to be an animated image asset.
 */
export const isLikelyAnimatedImageUrl = (url: string): boolean => {
  if (!url || typeof url !== 'string') return false

  return /\.(gif|webp)(\?|$)/i.test(url)
}

/**
 * Returns the best-effort media kind for a Cloudinary URL.
 */
export const getCloudinaryMediaKind = (url: string): 'image' | 'video' => {
  if (isLikelyVideoUrl(url)) return 'video'
  return 'image'
}

/**
 * Validates if a URL is a valid image URL (basic check)
 */
export const isValidImageUrl = (url: string): boolean => {
  if (!url || typeof url !== 'string') return false
  
  try {
    const urlObj = new URL(url)
    return urlObj.protocol === 'https:' || urlObj.protocol === 'http:'
  } catch {
    return false
  }
}

/**
 * Validates email format
 */
export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email)
}

/**
 * Validates password strength
 */
export const isValidPassword = (password: string): boolean => {
  return password.length >= 6
}

/**
 * Validates if a string is not empty after trimming
 */
export const isNotEmpty = (value: string): boolean => {
  return typeof value === 'string' && value.trim().length > 0
}

/**
 * Detects whether a validation warning list contains a critical format error.
 */
export const hasCriticalFormatValidationErrors = (
  warnings?: ValidationWarning[]
): boolean => {
  return warnings?.some(warning => warning.severity === 'error' && warning.type === 'format') || false
}